import type React from 'react'
import { Vertical, Horizontal, Texto, GraviButton, BBDTag, MaterialIcon } from '@gravitate-js/excalibrr'

interface Supplier {
  name: string
  count: number
}

interface StatusAggregationProps {
  title: string
  count: number
  percentage?: number
  topSuppliers: Supplier[]
  tagType?: 'error' | 'warning' | 'success' | 'info'
  onHeaderClick?: (event?: React.MouseEvent) => void
  onSupplierClick?: (supplierName: string, event?: React.MouseEvent) => void
  showTag?: boolean
  showDot?: boolean
  dotColor?: string
  style?: React.CSSProperties
}

export function StatusAggregation({
  title,
  count,
  percentage,
  topSuppliers,
  tagType = 'warning',
  onHeaderClick,
  onSupplierClick,
  showTag = true,
  showDot = false,
  dotColor,
  style,
}: StatusAggregationProps) {
  return (
    <Vertical alignItems='flex-start' style={{ width: '33.33%', ...style }}>
      <Horizontal
        justifyContent='space-between'
        alignItems='center'
        style={{
          borderBottom: '1px solid #e0e0e0',
          paddingBottom: '10px',
          marginBottom: '8px',
          paddingTop: '8px',
          width: '100%',
        }}
      >
        {showTag ? (
          <BBDTag {...{ [tagType]: true }} icon={<MaterialIcon icon='circle' fill size={8} className='mr-2' />}>
            <Texto category='p1' weight='bold' style={{ display: 'inline' }}>
              {title}
            </Texto>
          </BBDTag>
        ) : (
          <Horizontal alignItems='center' gap={8}>
            {showDot && <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor || '#DC3545' }} />}
            <Texto weight='bold' style={{ fontSize: '14px', color: '#212529' }}>
              {title}
            </Texto>
          </Horizontal>
        )}
        <Horizontal gap={6} alignItems='center'>
          <Texto category='p1' weight='bold' style={{ marginLeft: showTag ? 0 : 'auto' }}>
            {count}
          </Texto>
          {percentage !== undefined && (
            <Texto category='p1' style={{ fontSize: '12px', color: '#6B7280' }}>
              ({percentage.toFixed(0)}%)
            </Texto>
          )}
          {onHeaderClick && !showTag ? (
            <MaterialIcon
              icon='arrow_forward'
              size={16}
              style={{ color: '#6C757D', cursor: 'pointer' }}
              onClick={(e) => onHeaderClick(e)}
            />
          ) : onHeaderClick ? (
            <GraviButton
              appearance='link'
              size='small'
              style={{
                fontSize: '12px',
                color: '#666',
                padding: '0 4px',
                minWidth: 'auto',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              onClick={(e) => onHeaderClick(e)}
            >
              →
            </GraviButton>
          ) : null}
        </Horizontal>
      </Horizontal>

      {/* Breakdown */}
      <Vertical gap={2} style={{ width: '100%' }}>
        {topSuppliers.map((supplier, index) => (
          <Horizontal key={index} justifyContent='space-between' style={{ width: '100%' }}>
            {onSupplierClick ? (
              <GraviButton
                appearance='link'
                size='small'
                style={{
                  fontSize: '12px',
                  color: 'var(--gray-500)',
                  padding: '0',
                  minWidth: 'auto',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  height: 'auto',
                  lineHeight: 'normal',
                }}
                onClick={(e) => onSupplierClick(supplier.name, e)}
              >
                <Texto category='p2' appearance='secondary' style={{ fontSize: '12px', textDecoration: 'underline' }}>
                  {supplier.name}
                </Texto>
              </GraviButton>
            ) : (
              <Texto category='p2' appearance='secondary' style={{ fontSize: '12px' }}>
                {supplier.name}
              </Texto>
            )}
            <Texto category='p2' weight='600'>
              {supplier.count}
            </Texto>
          </Horizontal>
        ))}
      </Vertical>
    </Vertical>
  )
}
